import deepmerge from "deepmerge";
import { action, computed, IObservableValue, observable } from "mobx";
import React from "react";
import { fullAPI, partialAPI } from "../api";
import { authAPI } from "../api/auth";
import { CVPortalCandidateParsedToken } from "../api/auth/getCvPortalCandidateTokenForCurrentUser";
import { candidateAPI } from "../api/candidate";
import { documentsAPI } from "../api/documents";
import { jobsAPI } from "../api/jobs";
import { loggingAPI } from "../api/logging";
import { placesAPI } from "../api/places";
import { questionForAPI } from "../api/questionForm";
import { settingsAPI } from "../api/settings";
import { CvpSettings } from "../api/settings/CvpSettings";
import { typesAPI } from "../api/types";
import { getLocStore } from "../wsLocalization";
import { LocContext } from "../wsLocalization/LocContext";
import { AppType } from "./AppType";
import { storageArea } from "./storageArea";
import { UserInfoContext } from "./UserInfoContext";
import { useUserInfoBase } from "./useUserInfoBase";
import { removeRecursive } from "./util";
import { useLatestQuery } from "./useLatestQuery";
import { whoAmi } from "../api/auth/whoAmI";

export interface UserInfoContextProps {
  children?: any;
  settings: IObservableValue<CvpSettings>;
}

export const UserInfoContextProvider = ({
  settings,
  children,
}: UserInfoContextProps) => {
  const userInfo = useUserInfoBase(settings);

  return (
    <UserInfoContext.Provider value={userInfo}>
      {!!children && children}
    </UserInfoContext.Provider>
  );
};

export interface BaseUserInfoContextProps {
  loc: (s: string) => string;
  idFirmCandidate?: number;
  idCandidate?: number;
  children?: any;
  api: partialAPI;
  firmCandidates: FirmCandidateInfo[];
  reactCvEditor: boolean;

  readonly settings: CvpSettings;
  readonly idConcern: number;
  readonly locale: string;
}

export const createUserInfoContext = (
  getProps: () => BaseUserInfoContextProps
) => {
  const userInfo = new UserInfo(getProps);
  return userInfo;
};

export class UserInfo {
  declare getProps: () => BaseUserInfoContextProps;
  constructor(getProps: () => BaseUserInfoContextProps) {
    this.getProps = getProps;
  }

  // @observable userToken: CVPortalCandidateParsedToken = JSON.parse(
  //   storageArea.getItem("userToken") ?? "null"
  // );

  // @action setToken = (userToken: CVPortalCandidateParsedToken) => {
  //   this.userToken = userToken;

  //   storageArea.setItem(
  //     "userToken",
  //     userToken ? JSON.stringify(userToken) : null
  //   );
  // };

  @computed get locale() {
    return this.getProps().locale;
  }

  @computed get settings() {
    return this.getProps().settings;
  }

  @computed get idConcern() {
    return this.getProps().idConcern;
  }

  @computed get idFirmCandidate() {
    return this.getProps().idFirmCandidate;
  }

  @computed get idCandidate() {
    return this.getProps().idCandidate;
  }

  @computed get firmCandidates() {
    return this.getProps().firmCandidates;
  }

  @computed get reactCvEditor() {
    return !!this.getProps().reactCvEditor;
  }

  @computed get appType(): AppType {
    if (!this.settings.idEmbed) return AppType.cvWebtempNo;
    if (this.settings.idEmbed === "cvWebtempNo") return AppType.cvWebtempNo;

    if (this.settings.iframe) return AppType.iframe;
    return AppType.script;
  }

  @computed get currentFirmCandidate() {
    const idFirmCandidate = this.getProps().idFirmCandidate;
    const firm = this.getProps().firmCandidates?.find(
      (c) => c.id === idFirmCandidate
    );

    return firm;
  }

  @computed get idFirm() {
    const idFirmCandidate = this.getProps().idFirmCandidate;
    const firm = this.getProps().firmCandidates?.find(
      (c) => c.id === idFirmCandidate
    );

    return firm?.id_firma;
  }

  @computed get locStore() {
    return getLocStore(this, LocContext.GENERAL);
  }

  _fullAPI: fullAPI;
  get fullAPI() {
    if (this._fullAPI) return this._fullAPI;

    const implementedInThisProject: fullAPI = {
      candidateAPI,
      placesAPI,
      typesAPI,
      documentsAPI,
      questionFormAPI: questionForAPI,
      jobsAPI: jobsAPI,
      authAPI,
      loggingAPI,
      settingsAPI,
    };

    const implementedInParentProject: any =
      removeRecursive(this.getProps().api ?? {}, (v) => !v) ?? {};

    this._fullAPI = deepmerge(
      implementedInThisProject,
      implementedInParentProject
    );
    return this._fullAPI;
  }
}

export type SettingsChoices = { [key: string]: true };

export interface FirmCandidateInfo {
  id: number;
  id_firma: number;
  id_avdelinger: number;
  aktiv: string;
  avdelingsnavn: string;
  firmanavn: string;
}
